import { apiClient } from './apiClient'

/**
 * Global API Object
 *
 * All API methods for the application are defined here in a single file.
 * This prevents duplicate method names and makes code generation easier.
 *
 * Methods are added per-feature during migration.
 * Each method uses apiClient (axios instance) for HTTP requests.
 *
 * Naming convention: verb + entity (+ modifier)
 * Examples:
 * - getLanes, getLaneById
 * - saveLane
 * - getOrdersByStatus, updateOrderStatus
 *
 * @see IMPLEMENTATION_GUIDE.md Step 4 for adding API methods per feature
 * @see REACT_QUERY_IMPLEMENTATION.md for usage patterns
 */
export const api = {
  /**
   * ==========================================
   * LANES FEATURE API METHODS
   * ==========================================
   */

  /**
   * Get all lanes with optional search filter
   * @param {Object} params
   * @param {string} [params.search] - Optional search query
   * @returns {Promise<Array>} Array of lane objects
   */
  getLanes: async (params = {}) => {
    const { search } = params
    const { data } = await apiClient.get('/lanes', {
      params: search ? { search } : {},
    })
    return data
  },

  /**
   * Get a single lane by UID
   * REST Endpoint: GET /marketplace/lanes/:uid
   *
   * @param {string} uid - Lane UID
   * @returns {Promise<Object>} Lane object
   */
  getLaneByUid: async (uid) => {
    const { data } = await apiClient.get(`/lanes/${uid}`)
    return data
  },

  /**
   * Create a new lane or update an existing one
   * @param {Object} laneData - Lane object with all properties
   * @param {string} [laneData.uid] - If provided, updates existing lane (PATCH). If omitted, creates new lane (POST)
   * @returns {Promise<Object>} Created or updated lane object
   */
  saveLane: async (laneData) => {
    const method = laneData.uid ? 'patch' : 'post'
    const url = laneData.uid ? `/lanes/${laneData.uid}` : '/lanes/'
    const { data } = await apiClient[method](url, laneData)
    return data
  },

  /**
   * Get lane model definitions (schema/validation rules)
   * Used for form validation and dropdown options
   * @returns {Promise<Object>} Lane model definition
   */
  getLaneModelDefinitions: async () => {
    const { data } = await apiClient.get('/models/lane')
    return data
  },

  /**
   * Get leg model definitions (schema/validation rules)
   * Used for form validation and dropdown options for order legs
   * @returns {Promise<Object>} Leg model definitions
   */
  getLegModelDefinitions: async () => {
    const { data } = await apiClient.get('/models/component/orderlegs')
    return data
  },

  /**
   * Get coordinates (lat/lng) and timezone for an order-leg address.
   *
   * Sent as structured fields so the backend can scope the geocode by
   * country — explicit when the caller has one, otherwise inferred
   * server-side from state/zip. `state` is passed exactly as held (a Mexican
   * state is `Mexico/XX`); the backend strips the prefix.
   *
   * @param {Object} params
   * @param {string} params.street1
   * @param {string} params.city
   * @param {string} params.state
   * @param {string} params.zip
   * @param {string} [params.country] - ISO 3166-1 alpha-2 (e.g. "CA", "MX")
   * @returns {Promise<{coordinates: {latitude: number, longitude: number}, timezone: string, country?: string}>}
   *   `country` is the resolved alpha-2 code, absent for US
   */
  getCoordinatesByAddress: async (params) => {
    const { data } = await apiClient.get('/geo/coordinates', { params })
    return data
  },

  /**
   * Get detailed place information by Google Place ID
   * @param {string|number} placeId - Google Place ID
   * @returns {Promise<Object>} Place details object
   */
  getPlaceById: async (placeId) => {
    const { data } = await apiClient.post('/rpc/places.info', {
      id: `${placeId}`,
    })
    return data
  },

  /**
   * Activate a place
   * RPC Endpoint: places.activate
   *
   * @param {Object} params
   * @param {string} params.id - Place ID
   * @returns {Promise<Object>} Updated place object
   */
  activatePlace: async ({ id, ...payload }) => {
    const { data } = await apiClient.post('/rpc/places.activate', {
      id,
      ...payload,
    })
    return data
  },

  /**
   * Deactivate a place
   * RPC Endpoint: places.deactivate
   *
   * @param {Object} params
   * @param {string} params.id - Place ID
   * @returns {Promise<Object>} Updated place object
   */
  deactivatePlace: async ({ id, ...payload }) => {
    const { data } = await apiClient.post('/rpc/places.deactivate', {
      id,
      ...payload,
    })
    return data
  },

  /**
   * Update a place
   * RPC Endpoint: places.update
   *
   * @param {Object} params
   * @param {string} params.id - Place ID
   * @returns {Promise<Object>} Updated place object
   */
  updatePlace: async ({ id, ...payload }) => {
    const { data } = await apiClient.post('/rpc/places.update', {
      id,
      ...payload,
    })
    return data
  },

  /**
   * Create facility for a place
   * RPC Endpoint: places.facility.create
   *
   * @param {Object} params
   * @param {string} params.id - Place ID
   * @returns {Promise<Object>} Updated place object with facility
   */
  createPlaceFacility: async ({ id, ...payload }) => {
    const { data } = await apiClient.post('/rpc/places.facility.create', {
      id,
      ...payload,
    })
    return data
  },

  /**
   * Update facility for a place
   * RPC Endpoint: places.facility.update
   *
   * @param {Object} params
   * @param {string} params.id - Place ID
   * @returns {Promise<Object>} Updated place object with facility
   */
  updatePlaceFacility: async ({ id, ...payload }) => {
    const { data } = await apiClient.post('/rpc/places.facility.update', {
      id,
      ...payload,
    })
    return data
  },

  /**
   * Create geometry for a place
   * RPC Endpoint: places.geometry.create
   *
   * @param {Object} params
   * @param {string} params.id - Place ID
   * @returns {Promise<Object>} Updated place object with geometry
   */
  createPlaceGeometry: async ({ id, ...payload }) => {
    const { data } = await apiClient.post('/rpc/places.geometry.create', {
      id,
      ...payload,
    })
    return data
  },

  /**
   * Update geometry for a place
   * RPC Endpoint: places.geometry.update
   *
   * @param {Object} params
   * @param {string} params.id - Place ID
   * @returns {Promise<Object>} Updated place object with geometry
   */
  updatePlaceGeometry: async ({ id, ...payload }) => {
    const { data } = await apiClient.post('/rpc/places.geometry.update', {
      id,
      ...payload,
    })
    return data
  },

  /**
   * Edit a place (full update)
   * RPC Endpoint: places.edit
   *
   * @param {Object} params
   * @param {string} params.id - Place ID
   * @returns {Promise<Object>} Updated place object
   */
  editPlace: async ({ id, ...payload }) => {
    const { data } = await apiClient.post('/rpc/places.edit', {
      id,
      ...payload,
    })
    return data
  },

  /**
   * Create a new place
   * RPC Endpoint: places.create
   *
   * @param {Object} payload - Place data
   * @returns {Promise<Object>} Created place object
   */
  createPlace: async (payload) => {
    const { data } = await apiClient.post('/rpc/places.create', payload)
    return data
  },

  /**
   * Get list of places with pagination and filters
   * RPC Endpoint: places.list
   *
   * @param {Object} params
   * @param {boolean} [params.is_active] - Filter by active status
   * @param {number} [params.page] - Page number (0-based)
   * @param {number} [params.page_size] - Number of items per page
   * @returns {Promise<Object>} Object with places array and pagination info
   */
  getPlacesList: async (params) => {
    const { data } = await apiClient.post('/rpc/places.list', params)
    return data
  },

  /**
   * Delete a place
   * RPC Endpoint: places.delete
   *
   * @param {Object} params
   * @param {string} params.id - Place ID to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  deletePlace: async ({ id, ...payload }) => {
    const { data } = await apiClient.post('/rpc/places.delete', {
      id,
      ...payload,
    })
    return data
  },

  /**
   * List unlinked order leg locations
   * RPC Endpoint: places.listOrderLegLocations
   *
   * @returns {Promise<Array>} Array of unlinked order leg locations
   */
  listOrderLegLocations: async () => {
    const { data } = await apiClient.post(
      '/rpc/places.listOrderLegLocations',
      {},
    )
    return data
  },

  /**
   * Convert order leg location to a place
   * RPC Endpoint: places.convertOrderLegLocation
   *
   * @param {Object} payload
   * @param {string} payload.address_key - Address key of location to convert
   * @returns {Promise<Object>} Converted place object
   */
  convertOrderLegLocation: async (payload) => {
    const { data } = await apiClient.post(
      '/rpc/places.convertOrderLegLocation',
      payload,
    )
    return data
  },

  /**
   * Search for places by query string
   * RPC Endpoint: places.search
   *
   * @param {Object} params
   * @param {Object} params.query - Query object containing searchQuery and market
   * @param {string} params.query.searchQuery - Search query string
   * @param {string} params.query.market - Market UID to search within
   * @param {boolean} params.is_active - Whether to filter for active places only
   * @returns {Promise<Object>} Object with results array containing matching places
   */
  searchPlaces: async (params) => {
    const { data } = await apiClient.post('/rpc/places.search', params)
    return data
  },

  /**
   * ==========================================
   * TRANSLATIONS FEATURE API METHODS
   * ==========================================
   */

  /**
   * List translation rows. One row per (namespace, key, language); English and
   * Spanish are never paired. Blank filters are omitted because the endpoint
   * rejects an empty `search` or `key` with a 400.
   * REST Endpoint: GET /marketplace/translations
   *
   * @param {Object} [params]
   * @param {string} [params.namespace] - backend | mv-mobile | dnp-mobile | web-marketplace
   * @param {string} [params.language] - en | es
   * @param {string[]} [params.status] - Any of untranslated | needs_review | machine | approved
   * @param {string} [params.key] - Exact key match
   * @param {string} [params.search] - Case-insensitive match on key or value
   * @param {number} [params.page] - Page number (1-based)
   * @param {number} [params.perPage] - Items per page (max 500)
   * @returns {Promise<{ data: Array, totalCount: number }>} Rows and total count
   */
  getTranslations: async (params = {}) => {
    const { namespace, language, status, key, search, page, perPage } = params
    const trimmedKey = key?.trim()
    const trimmedSearch = search?.trim()
    const response = await apiClient.get('/translations', {
      params: {
        ...(namespace && { namespace }),
        ...(language && { language }),
        ...(status?.length && { status: status.join(',') }),
        ...(trimmedKey && { key: trimmedKey }),
        ...(trimmedSearch && { search: trimmedSearch }),
        ...(page && { page }),
        ...(perPage && { perPage }),
      },
    })
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-records'] || '0', 10),
    }
  },

  /**
   * Update a translation row. The server does not move `status` on its own,
   * and rejects a `value` whose {{placeholders}} differ from the English.
   * REST Endpoint: PATCH /marketplace/translations/:uid
   *
   * @param {Object} params
   * @param {string} params.uid - Translation UID
   * @param {string} [params.value] - New value, sent untrimmed
   * @param {string} [params.status] - New status
   * @returns {Promise<Object>} Updated translation row
   */
  updateTranslation: async ({ uid, ...changes }) => {
    const { data } = await apiClient.patch(`/translations/${uid}`, changes)
    return data
  },

  /**
   * Audit history of one translation row, newest first. `before` and `after`
   * are full row snapshots (`before` is null on create, `after` on delete).
   * `actor` is the user's name or email, null when `actorUid` is null (sync
   * writes) or resolves to no user.
   * REST Endpoint: GET /marketplace/translations/:uid/events
   *
   * @param {Object} params
   * @param {string} params.uid - Translation UID
   * @returns {Promise<Array<{ uid: string, action: string, before: Object|null, after: Object|null, context: Object|null, actorUid: string|null, actor: string|null, createdAt: string }>>}
   */
  getTranslationEvents: async ({ uid }) => {
    const { data } = await apiClient.get(`/translations/${uid}/events`)
    return data
  },

  /**
   * ==========================================
   * CONTAINERS FEATURE API METHODS
   * ==========================================
   */

  /**
   * Get list of containers with optional filters
   *
   * RPC Endpoint: containers.listContainers
   *
   * @param {Object} [params={}] - Query parameters
   * @param {Object} [params.filters] - Filter object
   * @param {Object} [params.filters.marketUid] - Market UID filter: { value: string[], op: 'ANY' }
   * @param {Object} [params.filters.containerType] - Container type filter: { value: string[], op: 'ANY' }
   * @param {number} [params.limit=200] - Maximum number of results
   *
   * @returns {Promise<Object>} Object containing:
   * - data: Array of container objects with containerNum, containerType, deliveryLocation, etc.
   * - containerTypes: Array of objects with containerType and count
   *
   * @example
   * // Get all containers in specific markets
   * const result = await api.listContainers({
   *   filters: {
   *     marketUid: { value: ['market-123', 'market-456'], op: 'ANY' }
   *   },
   *   limit: 200
   * })
   *
   * @example
   * // Get containers filtered by type
   * const result = await api.listContainers({
   *   filters: {
   *     containerType: { value: ['40HC', '20ST'], op: 'ANY' }
   *   }
   * })
   *
   * @example
   * // Get all containers (no filters)
   * const result = await api.listContainers()
   */
  listContainers: async (params = {}) => {
    const { filters = {}, limit = 200, sort, offset } = params

    const { data } = await apiClient.post('/rpc/containers.listContainers', {
      filters,
      limit,
      // The endpoint constrains sort.column to an enum and defaults to
      // firstAppointmentEnd ASC when omitted, so only send a sort we have one.
      ...(sort ? { sort } : {}),
      ...(offset != null ? { offset } : {}),
    })

    if (Array.isArray(data)) {
      const typeCounts = {}
      data.forEach((c) => {
        const type = c.containerType
        if (type) typeCounts[type] = (typeCounts[type] || 0) + 1
      })
      const containerTypes = Object.entries(typeCounts).map(
        ([containerType, count]) => ({ containerType, count }),
      )
      return { data, containerTypes }
    }

    return data
  },

  /**
   * List container types from the per-tenant catalog.
   *
   * RPC Endpoint: containers.list
   * Returns the catalog rows used to populate the Container Type dropdown
   * on the Order Form. Distinct from `listContainers`, which queries
   * physical containers for the Containers Dashboard.
   *
   * @returns {Promise<Array<{uid: string, name: string, domainUid: string}>>}
   */
  listContainerTypes: async () => {
    const { data } = await apiClient.post('/rpc/containers.list', {})
    return data
  },

  /**
   * ==========================================
   * SHARED/LIBRARY API METHODS
   * ==========================================
   */

  /**
   * Get all markets
   * @returns {Promise<Array>} Array of market objects
   */
  getMarkets: async () => {
    const { data } = await apiClient.get('/lib/markets')
    return data
  },

  /**
   * Get list of chassis types
   * RPC Endpoint: chassis.types.list
   *
   * @returns {Promise<Array>} Array of chassis type objects
   *
   * @example
   * const chassisTypes = await api.getChassisTypes()
   * // Returns: [{ name: '20ST', ... }, { name: '40HC', ... }]
   */
  getChassisTypes: async () => {
    const { data } = await apiClient.post('/rpc/chassis.types.list')
    return data
  },

  /**
   * Get users with optional filters
   * @param {Object} params
   * @param {string} [params.role] - User role filter (e.g., 'driver', 'admin')
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.perPage=10000] - Items per page
   * @param {string} [params.search] - Search query
   * @param {string} [params.ability] - Ability filter (e.g., 'canDrive')
   * @param {string} [params.companyUid] - Company UID filter
   * @returns {Promise<Array>} Array of user objects
   */
  getUsers: async (params = {}) => {
    const {
      role,
      page = 1,
      perPage = 10000,
      search,
      ability,
      companyUid,
    } = params
    const { data } = await apiClient.get('/users', {
      params: {
        ...(role && { role }),
        page,
        perPage,
        ...(search && { search }),
        ...(ability && { ability }),
        ...(companyUid && { companyUid }),
      },
    })
    return data
  },

  /**
   * Search users via RPC endpoint
   * Used for driver searches, admin user management, etc.
   *
   * RPC Endpoint: users.list.search
   *
   * @param {Object} params - Search parameters
   * @param {Object} [params.filters] - Filter object
   * @param {string} [params.filters.role] - User role (e.g., 'driver', 'admin')
   * @param {string} [params.filters.companyUid] - Company UID filter
   * @param {number|string} [params.page] - Page number (converted to string for API)
   * @param {number} [params.page_size] - Page size
   * @returns {Promise<Array>} Array of user objects
   *
   * @example
   * const drivers = await api.searchUsers({
   *   filters: { role: 'driver', companyUid: 'company-123' }
   * })
   */
  searchUsers: async (params = {}) => {
    const { data } = await apiClient.post('/rpc/users.list.search', params)
    return data
  },

  /**
   * Search users by filters (returns flat array)
   *
   * RPC Endpoint: users.search
   *
   * @param {Object} params - Search parameters
   * @param {Object} params.filters - Filter criteria
   * @param {string} [params.filters.role] - Role filter (e.g. 'driver')
   * @param {string} [params.filters.companyUid] - Company UID filter
   * @returns {Promise<Array>} Array of user objects
   */
  searchUsersByFilters: async (params = {}) => {
    const { data } = await apiClient.post('/rpc/users.search', params)
    return data
  },

  /**
   * ==========================================
   * ADMIN/COMPANIES FEATURE API METHODS
   * ==========================================
   */

  /**
   * Get paginated list of companies with filters
   *
   * RPC Endpoint: companies.list
   *
   * @param {Object} params - Query parameters
   * @param {boolean} [params.is_active] - Filter by active status
   * @param {number} [params.page=0] - Page number (0-indexed)
   * @param {number} [params.page_size=15] - Items per page
   * @returns {Promise<Object>} Object with items array and pagination
   *
   * @example
   * const result = await api.getCompaniesList({
   *   is_active: true,
   *   page: 0,
   *   page_size: 15
   * })
   */
  getCompaniesList: async (params = {}) => {
    const { data } = await apiClient.post('/rpc/companies.list', params)
    return data
  },

  /**
   * Search companies by query string
   *
   * RPC Endpoint: companies.search
   *
   * @param {Object} params - Search parameters
   * @param {string} params.query - Search query string
   * @param {boolean} [params.is_active] - Filter by active status
   * @param {number} [params.page=0] - Page number (0-indexed)
   * @param {number} [params.page_size=100] - Items per page
   * @returns {Promise<Object>} Object with items array and pagination
   *
   * @example
   * const result = await api.searchCompanies({
   *   query: 'acme',
   *   is_active: true,
   *   page: 0,
   *   page_size: 100
   * })
   */
  searchCompanies: async (params = {}) => {
    const { data } = await apiClient.post('/rpc/companies.search', params)
    return data
  },

  /**
   * Get detailed company information by ID
   *
   * RPC Endpoint: companies.info
   *
   * @param {string} companyUid - Company UID
   * @returns {Promise<Object>} Company details object
   *
   * @example
   * const company = await api.getCompanyInfo('company-123')
   */
  getCompanyInfo: async (companyUid) => {
    const { data } = await apiClient.post('/rpc/companies.info', {
      id: companyUid,
    })
    return data
  },

  /**
   * Create a new company
   *
   * RPC Endpoint: companies.create
   *
   * @param {Object} companyData - Company data
   * @param {string} companyData.name - Company name
   * @param {string} companyData.email - Company email
   * @param {Object} companyData.address - Company address
   * @returns {Promise<Object>} Created company object
   *
   * @example
   * const newCompany = await api.createCompany({
   *   name: 'ACME Corp',
   *   email: 'contact@acme.com',
   *   address: { street: '123 Main St', city: 'NYC', state: 'NY', zip: '10001' }
   * })
   */
  createCompany: async (companyData) => {
    const { data } = await apiClient.post('/rpc/companies.create', companyData)
    return data
  },

  /**
   * Update existing company
   *
   * RPC Endpoint: companies.update
   *
   * @param {Object} params
   * @param {string} params.id - Company UID
   * @param {Object} params.data - Update data
   * @returns {Promise<Object>} Updated company object
   *
   * @example
   * const updated = await api.updateCompany({
   *   id: 'company-123',
   *   data: { name: 'New Company Name' }
   * })
   */
  updateCompany: async ({ id, ...data }) => {
    const { data: responseData } = await apiClient.post(
      '/rpc/companies.update',
      { id, ...data },
    )
    return responseData
  },

  /**
   * Add contact to company
   *
   * RPC Endpoint: companies.contacts.add
   *
   * @param {Object} params
   * @param {string} params.id - Company UID
   * @param {Object} params.contact - Contact data
   * @returns {Promise<Object>} Updated company with new contact
   */
  addCompanyContact: async ({ id, ...contactData }) => {
    const { data } = await apiClient.post('/rpc/companies.contacts.add', {
      id,
      ...contactData,
    })
    return data
  },

  /**
   * Remove contact from company
   *
   * RPC Endpoint: companies.contacts.remove
   *
   * @param {Object} params
   * @param {string} params.id - Company UID
   * @param {number} params.index - Contact index in array (0-based)
   * @returns {Promise<Object>} Updated company
   */
  removeCompanyContact: async ({ id, index }) => {
    const { data } = await apiClient.post('/rpc/companies.contacts.remove', {
      id,
      index,
    })
    return data
  },

  /**
   * Update company contact
   *
   * RPC Endpoint: companies.contacts.edit
   *
   * @param {Object} params
   * @param {string} params.id - Company UID
   * @param {number} params.index - Contact index in array (0-based)
   * @param {Object} params.contact - Updated contact data
   * @returns {Promise<Object>} Updated company
   */
  updateCompanyContact: async ({ id, index, ...contactData }) => {
    const { data } = await apiClient.post('/rpc/companies.contacts.edit', {
      id,
      index,
      ...contactData,
    })
    return data
  },

  /**
   * Update company properties
   *
   * RPC Endpoint: companies.properties.update
   *
   * @param {Object} params
   * @param {string} params.id - Company UID
   * @param {Object} params.properties - Properties to update
   * @returns {Promise<Object>} Updated company
   */
  updateCompanyProperties: async ({ id, ...properties }) => {
    const { data } = await apiClient.post('/rpc/companies.properties.update', {
      id,
      ...properties,
    })
    return data
  },

  /**
   * Update company deactivation reasons
   *
   * RPC Endpoint: companies.bannedReasons.update
   *
   * @param {Object} params
   * @param {string} params.id - Company UID
   * @param {Array<string>} params.reasons - Array of deactivation reasons
   * @returns {Promise<Object>} Updated company
   */
  updateCompanyDeactivationReasons: async ({ id, bannedReasons }) => {
    const { data } = await apiClient.post(
      '/rpc/companies.bannedReasons.update',
      { id, bannedReasons },
    )
    return data
  },

  /**
   * Deactivate company
   *
   * RPC Endpoint: companies.deactivate
   *
   * @param {Object} params
   * @param {string} params.id - Company UID
   * @param {boolean} [params.confirmation] - Required by the RPC to disable the company
   * @returns {Promise<Object>} Updated company with is_active: false
   */
  deactivateCompany: async ({ id, ...payload }) => {
    const { data } = await apiClient.post('/rpc/companies.deactivate', {
      id,
      ...payload,
    })
    return data
  },

  /**
   * Activate company
   *
   * RPC Endpoint: companies.activate
   *
   * @param {string} companyUid - Company UID
   * @returns {Promise<Object>} Updated company with is_active: true
   */
  activateCompany: async (companyUid) => {
    const { data } = await apiClient.post('/rpc/companies.activate', {
      id: companyUid,
    })
    return data
  },

  /**
   * Update company permissions
   *
   * RPC Endpoint: companies.permissions.update
   *
   * @param {Object} params
   * @param {string} params.id - Company UID
   * @param {Object} params.permissions - Permissions object
   * @returns {Promise<Object>} Updated company
   */
  updateCompanyPermissions: async ({ id, permissions }) => {
    const { data } = await apiClient.post('/rpc/companies.permissions.update', {
      id,
      permissions,
    })
    return data
  },

  /**
   * Get all companies (for linking operations)
   *
   * REST Endpoint: GET /companies
   *
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.perPage=5000] - Items per page
   * @param {string} [params.search] - Search query
   * @returns {Promise<Array>} Array of all company objects
   */
  getAllCompanies: async (params = {}) => {
    const { page = 1, perPage = 5000, search } = params
    const { data } = await apiClient.get('/companies', {
      params: { page, perPage, ...(search && { search }) },
    })
    return data
  },

  /**
   * Create link between parent and child companies
   *
   * RPC Endpoint: companies.createCompanyLink
   *
   * @param {Object} params
   * @param {string} params.fromCompanyUid - Parent company UID
   * @param {string} params.toCompanyUid - Child company UID
   * @param {string} params.relation - Relation type (default: 'parent')
   * @returns {Promise<Object>} Link result
   */
  createCompanyLink: async ({
    fromCompanyUid,
    toCompanyUid,
    relation = 'parent',
  }) => {
    const { data } = await apiClient.post('/rpc/companies.createCompanyLink', {
      fromCompanyUid,
      toCompanyUid,
      relation,
    })
    return data
  },

  /**
   * Remove link between companies
   *
   * RPC Endpoint: companies.removeCompanyLink
   *
   * @param {Object} params
   * @param {string} params.id - Link ID to remove
   * @returns {Promise<Object>} Unlink result
   */
  removeCompanyLink: async ({ id }) => {
    const { data } = await apiClient.post('/rpc/companies.removeCompanyLink', {
      id,
    })
    return data
  },

  /**
   * List linked companies for a given company
   *
   * RPC Endpoint: companies.listLinkedCompanies
   *
   * @param {Object} params
   * @param {string} params.fromCompanyUid - Company UID to get links for
   * @param {string} [params.relation] - Relation type (e.g., 'parent')
   * @returns {Promise<Array>} Array of link objects with id, linkedCompanyUid, relation
   *
   * @example
   * const links = await api.listLinkedCompanies({
   *   fromCompanyUid: 'company-123',
   *   relation: 'parent'
   * })
   * // Returns: [{ id: 'link-789', linkedCompanyUid: 'child-456', relation: 'parent' }]
   */
  listLinkedCompanies: async (payload = {}) => {
    const { data } = await apiClient.post(
      '/rpc/companies.listLinkedCompanies',
      payload,
    )
    return data
  },

  /**
   * Get TMS ID records for one or more carrier companies
   *
   * RPC Endpoint: companies.tmsIds.get
   *
   * @param {Object} params
   * @param {Array<string>} params.companyUids - Carrier company UIDs
   * @returns {Promise<Object>} Object with a data array of TMS ID records
   *
   * @example
   * const response = await api.getCompanyTmsIds({
   *   companyUids: ['carrier-123']
   * })
   * // Returns: { data: [{ imcCompanyUid: 'imc-1', carrierCompanyUid: 'carrier-123',
   * //                     carrierScac: 'ABCD', tmsId: 'A3515221-1' }] }
   */
  getCompanyTmsIds: async (payload = {}) => {
    const { data } = await apiClient.post('/rpc/companies.tmsIds.get', payload)
    return data
  },

  /**
   * Create or update a TMS ID for an IMC/carrier pair
   *
   * RPC Endpoint: companies.tmsIds.upsert
   *
   * @param {Object} params
   * @param {string} params.imcCompanyUid - IMC company UID
   * @param {string} params.carrierCompanyUid - Carrier company UID
   * @param {string} params.carrierScac - Carrier SCAC
   * @param {string} params.tmsId - TMS ID
   * @returns {Promise<Object>} Upsert result
   */
  upsertCompanyTmsId: async (payload = {}) => {
    const { data } = await apiClient.post(
      '/rpc/companies.tmsIds.upsert',
      payload,
    )
    return data
  },

  /**
   * Remove a TMS ID from an IMC/carrier pair
   *
   * RPC Endpoint: companies.tmsIds.delete
   *
   * @param {Object} params
   * @param {string} params.imcCompanyUid - IMC company UID
   * @param {string} params.carrierCompanyUid - Carrier company UID
   * @param {string} params.tmsId - TMS ID to remove
   * @returns {Promise<Object>} Delete result
   */
  deleteCompanyTmsId: async (payload = {}) => {
    const { data } = await apiClient.post(
      '/rpc/companies.tmsIds.delete',
      payload,
    )
    return data
  },

  /**
   * List shared drivers for a parent company
   *
   * RPC Endpoint: drivers.shared.list
   *
   * @param {Object} params - Query parameters
   * @param {string} params.parentCompanyUid - Parent company UID
   * @returns {Promise<Object>} Object with items array of shared driver records
   */
  listSharedDrivers: async (params = {}) => {
    const { data } = await apiClient.post('/rpc/drivers.shared.list', params)
    return data
  },

  /**
   * Add drivers to the shared drivers list
   *
   * RPC Endpoint: drivers.shared.add
   *
   * @param {Object} params - Mutation parameters
   * @param {Array<string>} params.driverUserUids - Array of driver user UIDs to add
   * @param {string} params.parentCompanyUid - Parent company UID
   * @returns {Promise<Object>} Object with summary of created/alreadyExists counts
   */
  addSharedDrivers: async (params = {}) => {
    const { data } = await apiClient.post('/rpc/drivers.shared.add', params)
    return data
  },

  /**
   * Remove drivers from the shared drivers list
   *
   * RPC Endpoint: drivers.shared.remove
   *
   * @param {Object} params - Mutation parameters
   * @param {Array<string>} params.driverUserUids - Array of driver user UIDs to remove
   * @param {string} params.parentCompanyUid - Parent company UID
   * @returns {Promise<Object>} Object with summary of removed count
   */
  removeSharedDrivers: async (params = {}) => {
    const { data } = await apiClient.post('/rpc/drivers.shared.remove', params)
    return data
  },

  // ========================================
  // USERS API METHODS
  // ========================================

  /**
   * Get user details by UID (REST)
   * REST Endpoint: GET /users/:uid
   *
   * @param {string} uid - User UID
   * @returns {Promise<Object>} User data object
   */
  getUserById: async (uid) => {
    const { data } = await apiClient.get(`/users/${uid}`)
    return data
  },

  /**
   * Get paginated list of users
   * RPC Endpoint: users.list
   *
   * @param {Object} params - Query parameters
   * @param {number|string} [params.page] - Page number (converted to string for API)
   * @param {number} [params.page_size] - Page size
   * @param {boolean} [params.is_active] - Active/inactive filter
   * @returns {Promise<Object>} User list with pagination
   */
  getUsersList: async (params = {}) => {
    // Backend expects page as string
    const payload = {
      ...params,
      ...(params.page !== undefined && { page: String(params.page) }),
    }
    const { data } = await apiClient.post('/rpc/users.list', payload)
    return data
  },

  /**
   * Get user details by UID
   * RPC Endpoint: users.info
   *
   * @param {string} userUid - User unique identifier
   * @returns {Promise<Object>} User object
   */
  getUserInfo: async (userUid) => {
    const { data } = await apiClient.post('/rpc/users.info', { id: userUid })
    return data
  },

  /**
   * Create new user
   * RPC Endpoint: users.create
   *
   * @param {Object} userData - User data
   * @returns {Promise<Object>} Created user object
   */
  createUser: async (userData) => {
    const { data } = await apiClient.post('/rpc/users.create', userData)
    return data
  },

  /**
   * Update user basic information
   * RPC Endpoint: users.update
   *
   * @param {Object} params
   * @param {string} params.userUid - User UID
   * @param {Object} params.updates - Fields to update
   * @returns {Promise<Object>} Updated user object
   */
  updateUser: async ({ userUid, updates }) => {
    const { data } = await apiClient.post('/rpc/users.update', {
      id: userUid,
      ...updates,
    })
    return data
  },

  /**
   * Update user contact information
   * RPC Endpoint: users.contactInfo.update
   *
   * @param {Object} params
   * @param {string} params.userUid - User UID
   * @param {Object} params.contactInfo - Contact info updates
   * @returns {Promise<Object>} Updated user object
   */
  updateUserContactInfo: async ({ userUid, contactInfo }) => {
    const { data } = await apiClient.post('/rpc/users.contactInfo.update', {
      id: userUid,
      ...contactInfo,
    })
    return data
  },

  /**
   * Update user properties (driver-specific fields)
   * RPC Endpoint: users.properties.update
   *
   * @param {Object} params
   * @param {string} params.userUid - User UID
   * @param {Object} params.properties - Properties to update
   * @returns {Promise<Object>} Updated user object
   */
  updateUserProperties: async ({ userUid, properties }) => {
    const { data } = await apiClient.post('/rpc/users.properties.update', {
      id: userUid,
      ...properties,
    })
    return data
  },

  /**
   * Update user password
   * REST Endpoint: POST /marketplace/users/:uid/password
   *
   * @param {Object} params
   * @param {string} params.userUid - User UID
   * @param {Object} params.password - Password data object
   * @returns {Promise<Object>} Updated user object
   */
  updateUserPasswordKinde: async ({ userUid, password }) => {
    const { data } = await apiClient.post(
      `/users/${userUid}/password`,
      password,
    )
    return data
  },

  /**
   * Update user deactivation/banned reasons
   * RPC Endpoint: users.bannedReasons.update
   *
   * @param {Object} params
   * @param {string} params.userUid - User UID
   * @param {Array} params.bannedReasons - Deactivation reasons
   * @returns {Promise<Object>} Updated user object
   */
  updateUserBannedReasons: async ({ userUid, bannedReasons }) => {
    const { data } = await apiClient.post('/rpc/users.bannedReasons.update', {
      id: userUid,
      bannedReasons,
    })
    return data
  },

  /**
   * Deactivate user
   * RPC Endpoint: users.deactivate
   *
   * @param {string} userUid - User UID
   * @returns {Promise<Object>} Updated user object
   */
  deactivateUser: async (userUid) => {
    const { data } = await apiClient.post('/rpc/users.deactivate', {
      id: userUid,
    })
    return data
  },

  /**
   * Activate user
   * RPC Endpoint: users.activate
   *
   * @param {string} userUid - User UID
   * @returns {Promise<Object>} Updated user object
   */
  activateUser: async (userUid) => {
    const { data } = await apiClient.post('/rpc/users.activate', {
      id: userUid,
    })
    return data
  },

  /**
   * Update user permissions
   * RPC Endpoint: users.permissions.update
   *
   * @param {Object} params
   * @param {string} params.userUid - User UID
   * @param {Object} params.permissions - Permissions object with { permission, action }
   * @returns {Promise<Object>} Updated user object
   */
  updateUserPermissions: async ({ userUid, permissions }) => {
    const { data } = await apiClient.post('/rpc/users.permissions.update', {
      id: userUid,
      ...permissions, // Spread permissions to flatten structure (permission and action at root level)
    })
    return data
  },

  /**
   * Delete user
   * RPC Endpoint: users.delete
   *
   * @param {string} userUid - User UID
   * @returns {Promise<void>}
   */
  deleteUser: async (userUid) => {
    const { data } = await apiClient.post('/rpc/users.delete', {
      id: userUid,
    })
    return data
  },

  // ========================================
  // MARKETS API METHODS
  // ========================================

  /**
   * Get list of available markets (basic - no filters)
   * RPC Endpoint: markets.list
   *
   * @returns {Promise<Array>} Array of market objects
   */
  getMarketsList: async () => {
    const { data } = await apiClient.post('/rpc/markets.list')
    return data
  },

  /**
   * Get list of markets with filters (Admin)
   * RPC Endpoint: markets.list
   *
   * @param {Object} params
   * @param {boolean} [params.isActive] - Filter by active status
   * @param {string} [params.page] - Page number for pagination
   * @param {number} [params.page_size] - Number of items per page
   * @returns {Promise<Array>} Array of market objects
   */
  // markets.list has no LIMIT/OFFSET and no paging envelope — it returns every
  // market ordered by name. page/page_size used to be sent here and silently
  // discarded server-side, so they are not part of the contract.
  getMarketsListAdmin: async (params = {}) => {
    const { isActive } = params
    const { data } = await apiClient.post('/rpc/markets.list', { isActive })
    return data
  },

  /**
   * Get single market info by ID
   * RPC Endpoint: markets.info
   *
   * @param {string} marketUid - Market UID
   * @returns {Promise<Object>} Market object with full details
   */
  getMarketInfo: async (marketUid) => {
    const { data } = await apiClient.post('/rpc/markets.info', {
      id: marketUid,
    })
    return data
  },

  /**
   * Create new market
   * RPC Endpoint: markets.create
   *
   * @param {Object} marketData - Market data object
   * @returns {Promise<Object>} Created market object
   */
  createMarket: async (marketData) => {
    const { data } = await apiClient.post('/rpc/markets.create', marketData)
    return data
  },

  /**
   * Update market margins
   * RPC Endpoint: markets.margins.update
   *
   * @param {Object} payload - Update payload
   * @param {string} payload.id - Market UID
   * @param {Object} payload.margins - Margins object with float values
   * @returns {Promise<Object>} Updated market object
   */
  updateMarketMargins: async (payload) => {
    const { data } = await apiClient.post(
      '/rpc/markets.margins.update',
      payload,
    )
    return data
  },

  /**
   * Update market metadata/info
   * RPC Endpoint: markets.info.update
   *
   * @param {Object} payload - Update payload
   * @returns {Promise<Object>} Updated market object
   */
  updateMarketInfo: async (payload) => {
    const { data } = await apiClient.post('/rpc/markets.info.update', payload)
    return data
  },

  // ========================================
  // TELEMETRY API METHODS
  // ========================================

  /**
   * Get user telemetry data (driver-specific)
   * RPC Endpoint: telemetry.search (not telemetry.getUserTelemetry!)
   *
   * @param {string} userUid - User UID (driver)
   * @returns {Promise<Object>} Telemetry data
   */
  getUserTelemetry: async (userUid) => {
    const { data } = await apiClient.post('/rpc/telemetry.search', {
      driverUserUid: userUid,
    })
    return data
  },

  // ========================================
  // ADMIN/NOTIFICATIONS API METHODS
  // ========================================

  /**
   * Send push notifications to drivers
   * RPC Endpoint: notifications.send
   *
   * @param {Array<Object>} notifications - Array of notification objects
   * @param {string} notifications[].driverUid - Driver user UID
   * @param {string} notifications[].message - Notification message (max 1KB)
   * @param {string} notifications[].title - Notification title (max 80 chars)
   * @returns {Promise<Object>} Response data
   */
  sendDriverNotifications: async (notifications) => {
    const { data } = await apiClient.post(
      '/rpc/notifications.send',
      notifications,
    )
    return data
  },

  // ========================================
  // ORDERS FEATURE API METHODS
  // ========================================

  /**
   * Get order by UID
   * REST Endpoint: GET /marketplace/orders/{uid}
   *
   * @param {string} uid - Order UID
   * @returns {Promise<Object>} Order object with full details
   *
   * @example
   * const order = await api.getOrderByUid('order-123')
   */
  getOrderByUid: async (uid) => {
    const { data } = await apiClient.get(`/orders/${uid}`)
    return data
  },

  /**
   * Create or update an order
   * REST Endpoint: POST /marketplace/orders (create) OR PUT /marketplace/orders/{uid} (update)
   *
   * @param {Object} orderData - Order object
   * @param {string} [orderData.uid] - If provided, updates existing order (PUT). If omitted, creates new order (POST)
   * @returns {Promise<Object>} Created or updated order object
   *
   * @example
   * // Create new order
   * const newOrder = await api.saveOrder({ legs: [...], ... })
   *
   * @example
   * // Update existing order
   * const updated = await api.saveOrder({ uid: 'order-123', legs: [...], ... })
   */
  saveOrder: async (orderData) => {
    const method = orderData.uid ? 'put' : 'post'
    const url = orderData.uid ? `/orders/${orderData.uid}` : '/orders'
    const { data } = await apiClient[method](url, orderData)
    return data
  },

  /**
   * Duplicate an existing order
   * REST Endpoint: POST /marketplace/orders/{orderUid}/duplicate
   *
   * @param {string} orderUid - UID of order to duplicate
   * @param {Object} duplicateData - Data for duplicate order
   * @returns {Promise<Object>} Newly created duplicate order
   *
   * @example
   * const duplicate = await api.duplicateOrder('order-123', { legs: [...] })
   */
  duplicateOrder: async (orderUid, duplicateData) => {
    const { data } = await apiClient.post(
      `/orders/${orderUid}/duplicate`,
      duplicateData,
    )
    return data
  },

  /**
   * Save direct tender from draft order
   * REST Endpoint: POST /marketplace/orders/{uid}/direct-tender
   *
   * @param {Object} orderData - Order data
   * @param {string} orderData.uid - Order UID
   * @param {string} orderData.carrierCompanyUid - Carrier company UID
   * @returns {Promise<Object>} Updated tendered order
   *
   * @example
   * const tendered = await api.saveDirectTenderFromDraft({
   *   uid: 'order-123',
   *   carrierCompanyUid: 'carrier-456'
   * })
   */
  saveDirectTenderFromDraft: async (orderData) => {
    const { data } = await apiClient.post(
      `/orders/${orderData.uid}/direct-tender`,
      orderData,
    )
    return data
  },

  /**
   * One-shot create + tender for new (unsaved) orders
   * REST Endpoint: POST /marketplace/orders/direct-tender
   *
   * Creates the order and tenders it to a carrier in a single request.
   * Used by ModalView when tendering a brand-new order that has no uid yet.
   *
   * @param {Object} orderData - Full order payload including carrierCompanyUid
   * @returns {Promise<Object>} Created and tendered order
   */
  saveDirectTenderOrder: async (orderData) => {
    const { data } = await apiClient.post('/orders/direct-tender', orderData)
    return data
  },

  /**
   * Direct tender an already-published order (DrayNow flow)
   * REST Endpoint: POST /marketplace/orders/{uid}/tenders
   *
   * @param {Object} orderData - Tender data
   * @param {string} orderData.uid - Order UID
   * @param {string} orderData.carrierCompanyUid - Carrier company UID
   * @param {string} [orderData.driverUserUid] - Driver user UID (optional)
   * @returns {Promise<Object>} Tender response
   */
  directTender: async (orderData) => {
    const { data } = await apiClient.post(
      `/orders/${orderData.uid}/tenders`,
      orderData,
    )
    return data
  },

  /**
   * Validate unsaved order data
   * REST Endpoint: POST /marketplace/orders/validate
   *
   * @param {Object} orderData - Order data to validate
   * @returns {Promise<Object>} Validation result
   *
   * @example
   * const validation = await api.validateUnsavedOrder({ legs: [...] })
   */
  validateUnsavedOrder: async (orderData) => {
    const { data } = await apiClient.post('/orders/validate', orderData)
    return data
  },

  /**
   * Validate an existing (persisted) order server-side
   * REST Endpoint: GET /marketplace/orders/{uid}/validate
   *
   * The backend fetches the order, extracts its writable fields, and runs
   * the same full validation the tender endpoint performs before
   * transitioning a draft — so this gate cannot disagree with the tender.
   * Drafts with missing required fields reject with a 400 error array.
   *
   * @param {Object} params
   * @param {string} params.uid - Order UID
   * @returns {Promise<Object>} Validated order data (empty for non-drafts)
   *
   * @example
   * await api.validateExistingOrder({ uid: 'order-123' })
   */
  validateExistingOrder: async ({ uid }) => {
    const { data } = await apiClient.get(`/orders/${uid}/validate`)
    return data
  },

  /**
   * Dismiss alerts for an order
   * REST Endpoint: PATCH /marketplace/orders/{uid}/alert-dismissals
   *
   * @param {Object} params
   * @param {string} params.uid - Order UID
   * @param {Array} params.dismissals - Array of alert dismissals
   * @returns {Promise<Object>} Updated order
   *
   * @example
   * const updated = await api.patchOrderAlertDismissals({
   *   uid: 'order-123',
   *   dismissals: [{ alertType: 'ETA_LATE', reason: 'Traffic' }]
   * })
   */
  patchOrderAlertDismissals: async ({ uid, dismissals }) => {
    const { data } = await apiClient.patch(
      `/orders/${uid}/alert-dismissals`,
      dismissals,
    )
    return data
  },

  /**
   * Create street turn for order
   * REST Endpoint: PUT /marketplace/orders/{uid}/street-turn
   *
   * @param {Object} streetTurnData - Street turn data
   * @param {string} streetTurnData.uid - Order UID
   * @returns {Promise<Object>} Updated order with street turn
   *
   * @example
   * const result = await api.updateOrderStreetTurn({
   *   uid: 'order-123',
   *   containerNumber: 'CONT123'
   * })
   */
  updateOrderStreetTurn: async (streetTurnData) => {
    const { data } = await apiClient.put(
      `/orders/${streetTurnData.uid}/street-turn`,
      streetTurnData,
    )
    return data
  },

  /**
   * Remove street turn from order
   * REST Endpoint: PUT /marketplace/orders/{uid}/remove-street-turn
   *
   * @param {Object} params
   * @param {string} params.uid - Order UID
   * @returns {Promise<Object>} Updated order without street turn
   *
   * @example
   * const result = await api.removeOrderStreetTurn({ uid: 'order-123' })
   */
  removeOrderStreetTurn: async (params) => {
    const { data } = await apiClient.put(
      `/orders/${params.uid}/remove-street-turn`,
      params,
    )
    return data
  },

  /**
   * Get allowed accessorial types for orders
   * REST Endpoint: GET /marketplace/models/component/orderaccessorial
   *
   * @returns {Promise<Object>} Accessorial model definition with allowed types
   *
   * @example
   * const accessorials = await api.getOrderAccessorialTypes()
   * // Returns: { type: { allow: ['Detention', 'Chassis', ...] } }
   */
  getOrderAccessorialTypes: async () => {
    const { data } = await apiClient.get('/models/component/orderaccessorial')
    return data
  },

  /**
   * Get order document model definition
   * REST Endpoint: GET /marketplace/models/component/orderdocument
   *
   * Used for document upload validation and schema
   *
   * @returns {Promise<Object>} Document model definition
   *
   * @example
   * const docModel = await api.getOrderDocumentModel()
   */
  getOrderDocumentModel: async () => {
    const { data } = await apiClient.get('/models/component/orderdocument')
    return data
  },

  /**
   * Get leg step schema definitions for an order
   * RPC Endpoint: POST /rpc/orders.steps.schema.get
   *
   * @param {Object} params - Query parameters containing order data
   * @returns {Promise<Object>} Step schema definitions
   */
  getLegStepSchema: async (params) => {
    const { data } = await apiClient.post(
      '/rpc/orders.steps.schema.get',
      params,
    )
    return data
  },

  /**
   * Update order steps
   * RPC Endpoint: POST /rpc/orders.steps.update
   *
   * @param {Object} params - Step update data
   * @returns {Promise<Object>} Updated order steps
   */
  updateOrderSteps: async (params) => {
    const { data } = await apiClient.post('/rpc/orders.steps.update', params)
    return data
  },

  /**
   * Get US states and territories list
   * RPC Endpoint: POST /rpc/address.states.list
   *
   * Returns a dictionary of state/territory abbreviations and full names
   *
   * @returns {Promise<Object>} Dictionary of states (abbr -> full name)
   *
   * @example
   * const states = await api.getStatesTerritories()
   * // Returns: { CA: 'California', NY: 'New York', ... }
   */
  getStatesTerritories: async () => {
    const { data } = await apiClient.post('/rpc/address.states.list')
    return data
  },

  // ============================================================================
  // MARKETPLACE FEATURE API METHODS
  // ============================================================================

  /**
   * Get marketplace order counts by category
   * Endpoint: POST /orders/listOrdersCounts
   *
   * Returns counts for each order category (open, pending, tendered, etc.)
   * Used by MarketplaceSidebar to display category counts.
   *
   * @param {Object} params - Query parameters
   * @param {string} [params.search] - Search query
   * @param {string[]} [params.marketUids] - Filter by market UIDs
   * @param {Object} [params.filters] - Additional filter criteria
   * @returns {Promise<Object>} Category counts { open, pending, tendered, in_progress, finished }
   */
  getMarketplaceOrdersCounts: async (params = {}) => {
    const { search, marketUids, ...filters } = params
    const { data } = await apiClient.post('/orders/listOrdersCounts', {
      ...(search && { search }),
      ...(marketUids?.length && { marketUids }),
      ...filters,
    })
    return data
  },

  /**
   * Get marketplace orders list with pagination
   * Endpoint: POST /orders/listOrders
   *
   * Returns paginated orders filtered by category, markets, search, and custom filters.
   * Total count is extracted from x-total-records response header.
   *
   * @param {Object} params - Query parameters
   * @param {string} params.category - Order category ('open', 'pending', 'tendered', 'in_progress', 'finished')
   * @param {number} [params.page=1] - Page number (1-based)
   * @param {number} [params.perPage=30] - Items per page
   * @param {string[]} [params.marketUids] - Filter by market UIDs
   * @param {string} [params.search] - Search query
   * @param {string} [params.sort] - Sort configuration (JSON string)
   * @param {Object} [params.filters] - Additional filter criteria
   * @returns {Promise<{ data: Array, totalCount: number }>} Orders and total count
   */
  getMarketplaceOrders: async (params = {}) => {
    const {
      category,
      page = 1,
      perPage = 30,
      marketUids,
      search,
      sort,
      ...filters
    } = params
    const response = await apiClient.post('/orders/listOrders', {
      category,
      page,
      perPage,
      ...(marketUids?.length && { marketUids }),
      ...(search && { search }),
      ...(sort && { sort }),
      ...filters,
    })
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-records'] || '0', 10),
    }
  },

  /**
   * Get marketplace trip counts by category
   * Endpoint: POST /trips/listTripsCounts
   *
   * Returns counts for each trip category (open, pending, tendered, etc.)
   * Used by MarketplaceSidebar when viewing trips.
   *
   * @param {Object} params - Query parameters
   * @param {string} [params.search] - Search query
   * @param {string[]} [params.marketUids] - Filter by market UIDs
   * @param {Object} [params.filters] - Additional filter criteria
   * @returns {Promise<Object>} Category counts { open, pending, tendered, in_progress, finished }
   */
  getMarketplaceTripsCounts: async (params = {}) => {
    const { search, marketUids, ...filters } = params
    const { data } = await apiClient.post('/trips/listTripsCounts', {
      ...(search && { search }),
      ...(marketUids?.length && { marketUids }),
      ...filters,
    })
    return data
  },

  /**
   * Get marketplace trips list with pagination
   * Endpoint: POST /trips/listTrips
   *
   * Returns paginated trips filtered by category, markets, search, and custom filters.
   * Total count is extracted from x-total-records response header.
   *
   * @param {Object} params - Query parameters
   * @param {string} params.category - Trip category ('open', 'pending', 'tendered', 'in_progress', 'finished')
   * @param {number} [params.page=1] - Page number (1-based)
   * @param {number} [params.perPage=30] - Items per page
   * @param {string[]} [params.marketUids] - Filter by market UIDs
   * @param {string} [params.search] - Search query
   * @param {string} [params.sort] - Sort configuration (JSON string)
   * @param {Object} [params.filters] - Additional filter criteria
   * @returns {Promise<{ data: Array, totalCount: number }>} Trips and total count
   */
  getMarketplaceTrips: async (params = {}) => {
    const {
      category,
      page = 1,
      perPage = 30,
      marketUids,
      search,
      sort,
      ...filters
    } = params
    // Match original trip.js client payload structure:
    // - Spread filters at root level (not nested under 'filters' key)
    // - Pass marketUids as-is (undefined = omitted by JSON serialization)
    // - Pass search as-is (undefined = omitted by JSON serialization)
    const response = await apiClient.post('/trips/listTrips', {
      category,
      page,
      perPage,
      marketUids,
      search,
      sort,
      ...filters,
    })
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-records'] || '0', 10),
    }
  },

  /**
   * Get location events for an order
   * Endpoint: GET /orders/{uid}/location-events
   *
   * Returns GPS tracking events and location history for the order.
   * Used by ExpandedMapPreview to show driver location trail on map.
   *
   * @param {string} uid - Order UID
   * @returns {Promise<Array>} Array of location event objects with coordinates and timestamps
   */
  getOrderLocationEvents: async (uid) => {
    const { data } = await apiClient.get(`/orders/${uid}/location-events`)
    return data
  },

  /**
   * Get places/stops for an order
   * Endpoint: GET /orders/{uid}/places
   *
   * Returns detailed place information for all stops in the order.
   * Used by ExpandedMapPreview for detailed location markers.
   *
   * @param {string} uid - Order UID
   * @returns {Promise<Array>} Array of place objects with full address details
   */
  getOrderPlaces: async (uid) => {
    const { data } = await apiClient.get(`/orders/${uid}/places`)
    return data
  },

  /**
   * Get presigned URL for document upload
   * Endpoint: GET /document/uploadurlv2/{market}/{filename}/{documentId}
   *
   * Returns a presigned S3 URL for direct file upload.
   * Used by DocumentsModal for document uploads (file picker and drag-drop).
   *
   * @param {Object} params - Upload parameters
   * @param {string} params.market - Market name (NOT UID) — legacy uses localStorage('drayMarketName').
   *   URL-encoded into the path, so names containing '/' (e.g. "North Jersey/New York") stay a single route segment.
   * @param {string} params.filename - Original filename (will be sanitized)
   * @param {string} [params.documentId] - Optional document ID (generated if not provided)
   * @returns {Promise<{ presignedUrl: string, documentId: string }>} Upload URL and document ID
   */
  getDocumentUploadUrl: async ({ market, filename, documentId }) => {
    // Generate UUID if not provided
    const id = documentId || crypto.randomUUID()
    // Sanitize filename - remove special characters
    const cleanFilename = filename.replace(
      /[&$@=;:+,?{^}%`[\]'""'"'><~#|]/g,
      '',
    )
    const { data } = await apiClient.get(
      `/document/uploadurlv2/${encodeURIComponent(market)}/${encodeURIComponent(cleanFilename)}/${id}`,
    )
    return {
      ...data,
      presignedUrl: data.url || data.presignedUrl || data,
      documentId: id,
    }
  },

  /**
   * Get open street turn match count
   * RPC Endpoint: POST /rpc/streetTurns.getOpenStreetTurnMatchCount
   *
   * Returns the count of open street turn matches for the selected markets.
   * Used by MarketplaceSidebar to display the badge on "Manage Street Turns" link.
   *
   * @param {Object} params
   * @param {string[]} [params.marketUids] - Filter by market UIDs
   * @returns {Promise<Object>} Object with count property { count: number }
   */
  getOpenStreetTurnMatchCount: async (params = {}) => {
    const { marketUids } = params
    const { data } = await apiClient.post(
      '/rpc/streetTurns.getOpenStreetTurnMatchCount',
      {
        ...(marketUids?.length && { marketUids }),
      },
    )
    return data
  },

  /**
   * Get waitlist user details
   * Endpoint: POST /rpc/users.getUsersWaitlist
   *
   * Fetches user details for drivers on a trip waitlist.
   * Used by WaitlistCell to display driver names and phone numbers on hover.
   *
   * @param {Array<string>} waitlistUids - Array of user UIDs on waitlist
   * @returns {Promise<Array>} Array of user objects with firstName, lastName, phone, uid
   */
  getWaitlistUsers: async (waitlistUids) => {
    const { data } = await apiClient.post(
      '/rpc/users.getUsersWaitlist',
      waitlistUids,
    )
    return data
  },

  /**
   * Get order contact information (broker and company details)
   * Used by IMCCell to display contact info on hover.
   *
   * Fetches user info (broker) and company info in parallel.
   * Replicates the Order.getContactInformation() method from the MobX model.
   *
   * @param {Object} params
   * @param {string} [params.imcUserUid] - IMC user UID (broker)
   * @param {string} [params.imcCompanyUid] - IMC company UID
   * @param {boolean} [params.isEdi] - Whether order is from EDI
   * @returns {Promise<Object>} Contact info { name, email, phone, company, companyPhone }
   */
  getOrderContactInfo: async ({ imcUserUid, imcCompanyUid, isEdi }) => {
    const contactData = {}

    const promises = []

    // Fetch user info if imcUserUid exists
    if (imcUserUid) {
      promises.push(
        apiClient
          .get(`/users/${imcUserUid}`)
          .then((res) => {
            contactData.name =
              `${res.data.firstName || ''} ${res.data.lastName || ''}`.trim()
            contactData.email = res.data.email
            contactData.phone = res.data.phone
          })
          .catch(() => {
            // If user fetch fails, set a fallback name
            contactData.name = 'Unknown User'
          }),
      )
    } else {
      // No user UID - set appropriate fallback
      contactData.name = isEdi ? 'EDI Order' : 'Customer contact not set'
    }

    // Fetch company info if imcCompanyUid exists
    if (imcCompanyUid) {
      promises.push(
        apiClient
          .get(`/companies/${imcCompanyUid}`)
          .then((res) => {
            contactData.company = res.data.name
            contactData.companyPhone = res.data.phone
          })
          .catch(() => {
            // If company fetch fails, set a fallback
            contactData.company = 'Unknown Company'
          }),
      )
    }

    await Promise.all(promises)
    return contactData
  },

  /**
   * Dismiss/snooze alerts for an order or trip
   * Endpoint: PATCH /orders/{uid}/alert-dismissals OR /trips/{uid}/alert-dismissals
   *
   * Allows admins to snooze specific alerts for a period of time or indefinitely.
   * Used by AlertSnoozeModal to dismiss selected alerts.
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order or Trip UID
   * @param {Object} params.dismissals - Dismissal config { alertCode: { minutes: N } | { indefinite: true } }
   * @param {string} [params.entityType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Updated order/trip data
   */
  dismissOrderAlerts: async ({
    orderUid,
    dismissals,
    entityType = 'order',
  }) => {
    const endpoint = entityType === 'trip' ? 'trips' : 'orders'
    const { data } = await apiClient.patch(
      `/${endpoint}/${orderUid}/alert-dismissals`,
      dismissals,
    )
    return data
  },

  // ============================================================================
  // TRIPS API METHODS
  // ============================================================================

  /**
   * Get trip by UID
   * Endpoint: GET /trips/{uid}
   *
   * Returns full trip data including carrier minimum price settings.
   * Used by PriceModal to load trip data.
   *
   * @param {string} tripUid - Trip UID
   * @returns {Promise<Object>} Trip object with full details
   */
  getTripByUid: async (tripUid) => {
    const { data } = await apiClient.get(`/trips/${tripUid}`)
    return data
  },

  /**
   * Acknowledge order changes on a trip (carrier acknowledgment)
   * Endpoint: POST /trips/{tripUid}/change-acknowledgment
   *
   * Marks the trip's pending changeAcknowledgment as acknowledged by the
   * current user. Idempotent — acknowledging an already-acknowledged trip
   * returns the current acknowledgment state. No request body.
   *
   * @param {Object} params
   * @param {string} params.tripUid - Trip UID
   * @returns {Promise<Object>} Updated changeAcknowledgment object
   *   ({changes, changedAt, acknowledgedAt, acknowledgedBy})
   */
  acknowledgeTripChanges: async ({ tripUid }) => {
    const { data } = await apiClient.post(
      `/trips/${tripUid}/change-acknowledgment`,
    )
    return data
  },

  /**
   * Save/update a trip
   * Endpoint: PUT /trips/{uid}
   *
   * Used when saving trip-level data (e.g., appointment changes on a trip entity).
   *
   * @param {Object} tripData - Trip data including uid
   * @param {string} tripData.uid - Trip UID
   * @returns {Promise<Object>} Updated trip object
   */
  saveTrip: async (tripData) => {
    const { data } = await apiClient.put(`/trips/${tripData.uid}`, tripData)
    return data
  },

  /**
   * Save carrier minimum price for a trip
   * Endpoint: PATCH /trips/{tripUid}
   *
   * Updates the carrier minimum price and lock settings for a trip.
   * Used by PriceModal to save price changes.
   *
   * @param {Object} params
   * @param {string} params.tripUid - Trip UID
   * @param {number} params.carrierMinimumPrice - Minimum price value
   * @param {boolean} params.carrierMinimumPriceLock - Whether to lock the price
   * @returns {Promise<Object>} Updated trip object
   */
  saveCarrierMinimumPrice: async ({
    tripUid,
    carrierMinimumPrice,
    carrierMinimumPriceLock,
  }) => {
    const { data } = await apiClient.put(`/trips/${tripUid}`, {
      carrierMinimumPrice,
      carrierMinimumPriceLock,
    })
    return data
  },

  // ============================================================================
  // STREET TURNS API METHODS
  // ============================================================================

  /**
   * Remove interchange between orders
   * RPC Endpoint: POST /rpc/streetTurns.removeInterchange
   *
   * Removes the container interchange link between two orders.
   * Used by StreetTurnActionModal when removing an interchange.
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order UID to remove interchange from
   * @returns {Promise<Object>} Result of the removal operation
   */
  removeInterchange: async ({ orderUid }) => {
    const { data } = await apiClient.post(
      '/rpc/streetTurns.removeInterchange',
      {
        orderUid,
      },
    )
    return data
  },

  /**
   * Get orders affected by unbundling a street turn
   * RPC Endpoint: POST /rpc/streetTurns.unbundleOrders
   *
   * Returns the streetTurned and streetTurnedBy order objects
   * so the UI can display reassignment options before confirming.
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order UID to preview unbundle for
   * @returns {Promise<Object>} Object with streetTurned and streetTurnedBy order objects
   */
  getUnbundleOrders: async ({ orderUid }) => {
    const { data } = await apiClient.post('/rpc/streetTurns.unbundleOrders', {
      orderUid,
    })
    return data
  },

  /**
   * Unbundle a street turn
   * RPC Endpoint: POST /rpc/streetTurns.unbundle
   *
   * Removes the street turn bundle and applies the specified state
   * transitions to each affected order.
   *
   * @param {Object} payload
   * @param {string} payload.orderUid - Order UID to unbundle
   * @param {Object} payload.config - Per-order state transition config keyed by order UID
   * @returns {Promise<Object>} Result of the unbundle operation
   */
  unbundleStreetTurn: async (payload) => {
    const { data } = await apiClient.post('/rpc/streetTurns.unbundle', payload)
    return data
  },

  createInterchange: async (payload) => {
    const { data } = await apiClient.post(
      '/rpc/streetTurns.createInterchange',
      payload,
    )
    return data
  },

  listStreetTurnSuggestions: async (payload) => {
    const { data } = await apiClient.post(
      '/rpc/orders.streetTurns.list',
      payload,
    )
    return data
  },

  searchStreetTurns: async (payload) => {
    const { data } = await apiClient.post(
      '/rpc/orders.streetTurns.search',
      payload,
    )
    return data
  },

  geoCodeLegs: async (payload) => {
    const { data } = await apiClient.post('/rpc/geo.geoCodeLegs', payload)
    return data
  },

  getStreetTurnMatch: async (payload) => {
    const { data } = await apiClient.post('/rpc/streetTurns.getMatch', payload)
    return data
  },

  rejectStreetTurnMatch: async (payload) => {
    const { data } = await apiClient.post(
      '/rpc/streetTurns.rejectMatch',
      payload,
    )
    return data
  },

  reopenStreetTurnMatch: async (payload) => {
    const { data } = await apiClient.post(
      '/rpc/streetTurns.reopenMatch',
      payload,
    )
    return data
  },

  bundleStreetTurn: async (payload) => {
    const { data } = await apiClient.post('/rpc/streetTurns.bundle', payload)
    return data
  },

  // ============================================================================
  // ORDER ACTIONS API METHODS
  // ============================================================================

  /**
   * Perform an action on an order or trip
   * Endpoint: PUT /orders/{uid}/action/{action} OR /trips/{uid}/action/{action}
   *
   * Generic action executor. Actions include:
   * - approveAccessorials: Approve accessorial charges
   * - disputeAccessorials: Dispute accessorial charges
   * - cancel: Cancel an order (requires cancelReason)
   * - delete: Delete an order
   * - approveCarrierRequest: Approve carrier's request
   * - refuseTenderRequest: Reject a tender request (requires reason, note)
   * - approveTenderRequest: Accept a tender request (optional driverUserUid)
   * - assignDriver: Assign a driver (requires driverUserUid)
   * - removeDriver: Remove assigned driver
   * - carrierRequest: Request carrier assignment (requires driverUserUid)
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order or Trip UID
   * @param {string} params.action - Action type
   * @param {Object} [params.payload] - Action-specific payload
   * @param {string} [params.entityType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Updated order/trip or action result
   */
  performOrderAction: async ({
    orderUid,
    action,
    payload = {},
    entityType = 'order',
  }) => {
    const endpoint = entityType === 'trip' ? 'trips' : 'orders'
    const { data } = await apiClient.put(
      `/${endpoint}/${orderUid}/action/${action}`,
      payload,
    )
    return data
  },

  /**
   * Cancel an order or trip
   * Endpoint: PUT /orders/{uid}/action/cancel OR /trips/{uid}/action/cancel
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order or Trip UID
   * @param {string} params.cancelReason - Reason for cancellation
   * @param {string} [params.entityType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Cancelled order/trip
   */
  cancelOrder: async ({ orderUid, cancelReason, entityType = 'order' }) => {
    const endpoint = entityType === 'trip' ? 'trips' : 'orders'
    const { data } = await apiClient.put(
      `/${endpoint}/${orderUid}/action/cancel`,
      {
        cancelReason,
      },
    )
    return data
  },

  /**
   * Delete an order or trip
   * Endpoint: PUT /orders/{uid}/action/delete OR /trips/{uid}/action/delete
   *
   * @param {Object} params
   * @param {string} params.uid - Order or Trip UID
   * @param {string} [params.entityType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Deletion result
   */
  deleteOrder: async ({ uid, entityType = 'order' }) => {
    const endpoint = entityType === 'trip' ? 'trips' : 'orders'
    const { data } = await apiClient.put(`/${endpoint}/${uid}/action/delete`)
    return data
  },

  /**
   * Approve carrier's request for an order or trip
   * Endpoint: PUT /orders/{uid}/action/approveCarrierRequest OR /trips/{uid}/action/approveCarrierRequest
   *
   * @param {Object} params
   * @param {string} params.uid - Order or Trip UID
   * @param {string} [params.entityType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Updated order/trip
   */
  approveCarrierRequest: async ({ uid, entityType = 'order' }) => {
    const endpoint = entityType === 'trip' ? 'trips' : 'orders'
    const { data } = await apiClient.put(
      `/${endpoint}/${uid}/action/approveCarrierRequest`,
    )
    return data
  },

  /**
   * Refuse/reject a tender request
   * Endpoint: PUT /orders/{uid}/action/refuseTenderRequest OR /trips/{uid}/action/refuseTenderRequest
   *
   * Used by carriers to reject a tendered trip.
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order or Trip UID
   * @param {string} params.reason - Rejection reason
   * @param {string} [params.note] - Additional note
   * @param {string} [params.entityType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Updated order/trip
   */
  refuseTenderRequest: async ({
    orderUid,
    reason,
    note,
    entityType = 'order',
  }) => {
    const endpoint = entityType === 'trip' ? 'trips' : 'orders'
    const { data } = await apiClient.put(
      `/${endpoint}/${orderUid}/action/refuseTenderRequest`,
      { reason, note },
    )
    return data
  },

  /**
   * Accept/approve a tender request
   * Endpoint: PUT /orders/{uid}/action/approveTenderRequest OR /trips/{uid}/action/approveTenderRequest
   *
   * Used by carriers to accept a tendered trip.
   * Can optionally assign a driver immediately.
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order or Trip UID
   * @param {string} [params.driverUserUid] - Optional driver to assign
   * @param {string} [params.entityType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Updated order/trip
   */
  approveTenderRequest: async ({
    orderUid,
    driverUserUid,
    entityType = 'order',
  }) => {
    const endpoint = entityType === 'trip' ? 'trips' : 'orders'
    const { data } = await apiClient.put(
      `/${endpoint}/${orderUid}/action/approveTenderRequest`,
      driverUserUid ? { driverUserUid } : {},
    )
    return data
  },

  /**
   * Assign a driver to an order/trip
   * Endpoint: PUT /orders/{uid}/action/assignDriver OR /trips/{uid}/action/assignDriver
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order or Trip UID
   * @param {string} params.driverUserUid - Driver user UID to assign
   * @param {string} [params.entityType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Updated order/trip
   */
  assignDriver: async ({ orderUid, driverUserUid, entityType = 'order' }) => {
    const endpoint = entityType === 'trip' ? 'trips' : 'orders'
    const { data } = await apiClient.put(
      `/${endpoint}/${orderUid}/action/assignDriver`,
      { driverUserUid },
    )
    return data
  },

  /**
   * Remove driver from an order or trip
   * Endpoint: PUT /orders/{uid}/action/removeDriver OR /trips/{uid}/action/removeDriver
   *
   * The endpoint is determined at call time based on entityType, ensuring the
   * correct API is called even when React memoization or hook timing issues occur.
   *
   * @param {Object} params
   * @param {string} params.uid - Order or Trip UID
   * @param {string} [params.entityType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Updated order or trip
   */
  removeDriver: async ({ uid, entityType = 'order' }) => {
    const endpoint = entityType === 'trip' ? 'trips' : 'orders'
    const { data } = await apiClient.put(
      `/${endpoint}/${uid}/action/removeDriver`,
    )
    return data
  },

  /**
   * Request carrier assignment (with driver)
   * Endpoint: PUT /orders/{uid}/action/carrierRequest OR /trips/{uid}/action/carrierRequest
   *
   * Used when a carrier requests to be assigned to an order.
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order or Trip UID
   * @param {string} params.driverUserUid - Driver user UID
   * @param {string} [params.entityType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Updated order/trip
   */
  carrierRequest: async ({ orderUid, driverUserUid, entityType = 'order' }) => {
    const endpoint = entityType === 'trip' ? 'trips' : 'orders'
    const { data } = await apiClient.put(
      `/${endpoint}/${orderUid}/action/carrierRequest`,
      { driverUserUid },
    )
    return data
  },

  /**
   * Update order/trip administration data
   * Uses fetch-then-save pattern: GET current entity, merge field update, PUT back.
   * (The PATCH /administration endpoint does not exist on the backend.)
   *
   * Updates administrative fields on an order like:
   * - detailsConfirmed
   * - needsBol
   * - railBillingConfirmed
   * - finishedBlockers
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order or Trip UID
   * @param {string} params.field - Field name to update
   * @param {*} params.value - New value for the field
   * @param {string} [params.entityType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Updated order/trip
   */
  updateOrderAdministration: async ({
    orderUid,
    field,
    value,
    entityType = 'order',
  }) => {
    const isTrip = entityType === 'trip'
    const endpoint = isTrip ? 'trips' : 'orders'
    // 1. Fetch current entity to get fresh administration data
    const { data: current } = await apiClient.get(`/${endpoint}/${orderUid}`)
    // 2. Merge the field update into administration
    const payload = {
      uid: orderUid,
      administration: {
        ...current.administration,
        [field]: value,
      },
    }
    // 3. Save via PUT (same as saveOrder/saveTrip pattern)
    const { data } = await apiClient.put(`/${endpoint}/${orderUid}`, payload)
    return data
  },

  /**
   * Get active drivers for current user's company
   * RPC Endpoint: POST /rpc/users.list.search
   *
   * Used to check if there are any active drivers before showing
   * driver assignment buttons.
   *
   * @returns {Promise<Object>} Object with results array of active drivers
   */
  getActiveDrivers: async () => {
    const { data } = await apiClient.post('/rpc/users.list.search', {
      is_active: true,
      user_role: 'driver',
      query: 'driver',
    })
    return data
  },

  // ============================================================================
  // INVOICING API METHODS
  // ============================================================================

  /**
   * Create invoice for an order
   * RPC Endpoint: POST /rpc/orders.invoice.create
   *
   * Triggers the main invoicing process for a closed order.
   *
   * @param {string} orderUid - Order UID
   * @returns {Promise<Object>} Invoice creation result
   */
  createInvoice: async (orderUid) => {
    const { data } = await apiClient.post('/rpc/orders.invoice.create', {
      orderUid,
    })
    return data
  },

  /**
   * Create sales order for an order
   * RPC Endpoint: POST /rpc/invoice.createSalesOrder
   *
   * Creates a sales order in the accounting system.
   *
   * @param {string} orderUid - Order UID
   * @returns {Promise<Object>} Sales order creation result
   */
  createSalesOrder: async (orderUid) => {
    const { data } = await apiClient.post('/rpc/invoice.createSalesOrder', {
      orderUid,
    })
    return data
  },

  /**
   * Create vendor bill for an order
   * RPC Endpoint: POST /rpc/invoice.createVendorBill
   *
   * Creates a vendor bill in the accounting system.
   *
   * @param {string} orderUid - Order UID
   * @returns {Promise<Object>} Vendor bill creation result
   */
  createVendorBill: async (orderUid) => {
    const { data } = await apiClient.post('/rpc/invoice.createVendorBill', {
      orderUid,
    })
    return data
  },

  /**
   * Send EDI invoice for an order
   * RPC Endpoint: POST /rpc/invoice.sendEDIInvoice
   *
   * Sends invoice via EDI for orders that have EDI integration.
   *
   * @param {string} orderUid - Order UID
   * @returns {Promise<Object>} EDI invoice result
   */
  sendEDIInvoice: async (orderUid) => {
    const { data } = await apiClient.post('/rpc/invoice.sendEDIInvoice', {
      orderUid,
    })
    return data
  },

  /**
   * Search for drivers by company
   * RPC Endpoint: POST /rpc/users.list.search
   *
   * Used by driver search inputs to find drivers for assignment.
   *
   * @param {Object} params
   * @param {string} [params.companyUid] - Company UID to filter drivers
   * @param {string} [params.marketUid] - Market UID to filter drivers
   * @param {string} [params.search] - Search query
   * @returns {Promise<Array>} Array of driver objects
   */
  searchDrivers: async ({ companyUid, marketUid, search } = {}) => {
    const { data } = await apiClient.post('/rpc/users.list.search', {
      filters: {
        role: 'driver',
        ...(companyUid && { companyUid }),
        ...(marketUid && { marketUid }),
      },
      ...(search && { search }),
    })
    return data
  },

  /**
   * Get cancel reasons for an order
   * RPC Endpoint: POST /rpc/orders.cancelReasons
   *
   * Returns the list of valid cancellation reasons.
   *
   * @returns {Promise<Array>} Array of cancel reason options
   */
  getCancelReasons: async () => {
    const { data } = await apiClient.post('/rpc/orders.cancelReasons')
    return data
  },

  /**
   * Get carrier rejection reasons
   * RPC Endpoint: POST /rpc/carriers.rejectionReasons
   *
   * Returns the list of valid carrier rejection reasons.
   * Used when rejecting a tender request.
   *
   * @returns {Promise<Array>} Array of rejection reason options
   */
  getCarrierRejectionReasons: async () => {
    const { data } = await apiClient.post('/rpc/carriers.rejectionReasons')
    return data
  },

  // ============================================================================
  // ACCESSORIALS API METHODS
  // ============================================================================

  /**
   * Get allowed accessorial types for orders
   * REST Endpoint: GET /marketplace/models/component/orderaccessorial
   *
   * Returns accessorial type definitions including properties schema and document requirements.
   * The response structure is: { type: { allow: { [typeKey]: { name, propertiesSchema, documents, ... } } } }
   *
   * @returns {Promise<Object>} Object mapping accessorial type keys to their definitions
   *
   * @example
   * const types = await api.getAccessorialTypes()
   * // Returns: { detention: { name: 'Detention', propertiesSchema: {...} }, ... }
   */
  getAccessorialTypes: async () => {
    const { data } = await apiClient.get('/models/component/orderaccessorial')
    return data?.type?.allow || {}
  },

  /**
   * Create a new accessorial for an order
   * RPC Endpoint: POST /rpc/orders.accessorials.create
   *
   * Creates a new accessorial entry on an order.
   *
   * @param {Object} payload - Accessorial data
   * @param {string} payload.orderUid - Order UID
   * @param {string} payload.type - Accessorial type key
   * @param {Object} [payload.properties] - Type-specific properties
   * @param {number} [payload.legIndex] - Leg/stop index
   * @param {number} [payload.imcPriceOverride] - IMC price override
   * @param {number} [payload.carrierPriceOverride] - Carrier price override
   * @param {Array} [payload.documents] - Associated documents
   * @param {string} [payload.note] - Notes
   * @returns {Promise<Object>} Created accessorial object
   *
   * @example
   * const accessorial = await api.createAccessorial({
   *   orderUid: 'order-123',
   *   type: 'detention',
   *   properties: { hours: 2 },
   *   note: 'Delayed at pickup'
   * })
   */
  createAccessorial: async (payload) => {
    const { data } = await apiClient.post(
      '/rpc/orders.accessorials.create',
      payload,
    )
    return data
  },

  /**
   * Update an existing accessorial
   * RPC Endpoint: POST /rpc/orders.accessorials.update
   *
   * Updates an existing accessorial entry on an order.
   *
   * @param {Object} payload - Accessorial data with id
   * @param {string} payload.id - Accessorial ID
   * @param {string} payload.orderUid - Order UID
   * @param {Object} [payload.properties] - Updated properties
   * @param {number} [payload.imcPriceOverride] - IMC price override
   * @param {number} [payload.carrierPriceOverride] - Carrier price override
   * @param {Array} [payload.documents] - Updated documents
   * @param {string} [payload.note] - Updated notes
   * @returns {Promise<Object>} Updated accessorial object
   */
  updateAccessorial: async (payload) => {
    const { data } = await apiClient.post(
      '/rpc/orders.accessorials.update',
      payload,
    )
    return data
  },

  /**
   * Delete an accessorial from an order
   * RPC Endpoint: POST /rpc/orders.accessorials.delete
   *
   * Removes an accessorial entry from an order.
   *
   * @param {Object} payload - Delete params
   * @param {string} payload.id - Accessorial ID to delete
   * @param {string} payload.orderUid - Order UID
   * @returns {Promise<Object>} Deletion result
   */
  deleteAccessorial: async (payload) => {
    const { data } = await apiClient.post(
      '/rpc/orders.accessorials.delete',
      payload,
    )
    return data
  },

  // ============================================================================
  // DOCUMENTS API METHODS
  // ============================================================================

  /**
   * Add a document reference to an order
   *
   * For orders: saves via PUT /orders/{uid} with full documents array
   * For trips: saves via POST /rpc/trips.documents.create
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order or Trip UID
   * @param {string} [params.isType='order'] - Entity type: 'order' or 'trip'
   * @param {Array} [params.existingDocuments] - Current documents array (needed for orders)
   * @param {string} params.id - Document ID (from upload)
   * @param {string} params.type - Document type key
   * @param {string} params.key - S3 key
   * @param {string} params.publicUrl - Public URL
   * @param {string} params.uploadedBy - Uploader UID
   * @param {string} params.uploadedByFullName - Uploader full name
   * @param {number} params.size - File size in MB
   * @param {string} params.companyUid - Uploader company UID
   * @returns {Promise<Object>} Updated order/trip
   */
  /**
   * Save order documents — mirrors legacy Order.saveDocuments().
   * PUT /orders/{uid} with { uid, documents }.
   * documents must be the FULL array (existing + new), each serialized via serializeDocument.
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order UID
   * @param {Array} params.documents - Full documents array (serialized)
   * @returns {Promise<Object>} Updated order
   */
  saveOrderDocuments: async ({ orderUid, documents }) => {
    const { data } = await apiClient.put(`/orders/${orderUid}`, {
      uid: orderUid,
      documents,
    })
    return data
  },

  /**
   * Add documents to a trip — mirrors legacy Trip.addDocument().
   * POST /rpc/trips.documents.create
   *
   * @param {Object} params
   * @param {string} params.tripUid - Trip UID
   * @param {Array} params.documents - Array of document objects to add
   * @returns {Promise<Object>} Result
   */
  addTripDocuments: async ({ tripUid, documents }) => {
    const { data } = await apiClient.post('/rpc/trips.documents.create', {
      tripUid,
      documents,
    })
    return data
  },

  /**
   * Remove a document from a trip — mirrors legacy Trip.deleteDocument().
   * POST /rpc/trips.documents.remove
   *
   * @param {Object} params
   * @param {string} params.tripUid - Trip UID
   * @param {string} params.documentUid - Document UID to remove
   * @returns {Promise<Object>} Result
   */
  removeTripDocument: async ({ tripUid, documentUid }) => {
    const { data } = await apiClient.post('/rpc/trips.documents.remove', {
      tripUid,
      documentUid,
    })
    return data
  },

  /**
   * Remove a document reference from an order or trip via RPC
   *
   * For orders: POST /rpc/orders.documents.remove
   * For trips: POST /rpc/trips.documents.remove
   *
   * @param {Object} params
   * @param {string} params.orderUid - Order or Trip UID
   * @param {string} params.documentUid - Document UID to remove
   * @param {string} [params.isType='order'] - Entity type: 'order' or 'trip'
   * @returns {Promise<Object>} Result of the removal
   */
  removeOrderDocument: async ({ orderUid, documentUid }) => {
    const { data } = await apiClient.post('/rpc/orders.documents.remove', {
      orderUid,
      documentUid,
    })
    return data
  },

  /**
   * Delete a document from storage (sls-document service)
   * Endpoint: POST /document/delete
   *
   * Deletes the actual document file from S3 storage.
   * Called after removing the order reference to clean up storage.
   *
   * @param {Object} params
   * @param {string} params.id - Document ID
   * @returns {Promise<Object>} Deletion result
   */
  deleteDocument: async ({ id }) => {
    const { data } = await apiClient.delete(`/document/delete/${id}`)
    return data
  },

  /**
   * Get signed URL for document download
   * REST Endpoint: POST /marketplace/document/download
   *
   * Returns a signed S3 URL for downloading a document.
   *
   * @param {Object} params
   * @param {string} params.id - Document ID
   * @param {string} [params.key] - Document S3 key
   * @param {string} [params.orderUid] - Order UID
   * @returns {Promise<Object>} Object with signedUrl property
   */
  downloadDocument: async ({ id, key, orderUid }) => {
    const { data } = await apiClient.post('/document/download', {
      id,
      key,
      orderUid,
    })
    return data
  },

  /**
   * ==========================================
   * TENANT / CONFIG API METHODS
   * ==========================================
   */

  /**
   * Get tenant configuration (theme, features, policy URLs)
   * RPC Endpoint: getConfig
   * @returns {Promise<Object>} Tenant config object
   */
  getTenantConfig: async () => {
    const { data } = await apiClient.post('/rpc/tenant.getConfig', {})
    return data
  },

  /**
   * Get tenant configuration from public endpoint (no auth required)
   * Used on app boot before authentication to load theme, features, and policy URLs.
   * RPC Endpoint: rpc-public/tenant.getConfig
   * @returns {Promise<Object>} Tenant config object
   */
  getTenantPublicConfig: async () => {
    const { data } = await apiClient.post('/rpc-public/tenant.getConfig', {})
    return data
  },

  /**
   * Get Intercom token for user (server reads uid from auth header)
   * @returns {Promise<Object>} Object with token property
   */
  getIntercomToken: async () => {
    const { data } = await apiClient.get('/users/intercom/token')
    return data
  },

  /**
   * ==========================================
   * STREET TURNS API METHODS (additional)
   * ==========================================
   */

  /**
   * List street turn matches
   * RPC Endpoint: streetTurns.listMatches
   * @param {Object} params - Filter parameters
   * @returns {Promise<Array>} Array of street turn matches
   */
  listStreetTurnMatches: async (params = {}) => {
    const { data } = await apiClient.post(
      '/rpc/streetTurns.listMatches',
      params,
    )
    return data
  },

  /**
   * ==========================================
   * GEO API METHODS (additional)
   * ==========================================
   */

  /**
   * Get trip distance from coordinate pairs
   * @param {Array} coordinatePairs - Array of {latitude, longitude} objects
   * @returns {Promise<Object>} Object with tripDistance property
   */
  getTripDistance: async (coordinatePairs) => {
    const { data } = await apiClient.post('/geo/distance', {
      coordinates: coordinatePairs,
    })
    return data
  },

  /**
   * ==========================================
   * USER SEARCH API METHODS (additional)
   * ==========================================
   */

  /**
   * Search driver users with pagination (RPC-style flat params)
   * Used by SearchInputDriver component
   * RPC Endpoint: users.list.search
   * @param {Object} params - Search parameters
   * @param {string} params.query - Search query
   * @param {boolean} [params.is_active] - Filter active users
   * @param {boolean} [params.active_companies_only] - Filter active companies
   * @param {string} [params.user_role] - User role filter
   * @param {string} [params.company_uid] - Company UID filter
   * @param {string} [params.marketUid] - Market UID filter
   * @param {number} [params.page] - Page number
   * @param {number} [params.page_size] - Page size
   * @returns {Promise<Object>} Object with results array and total count
   */
  searchDriverUsers: async (params = {}) => {
    const { data } = await apiClient.post('/rpc/users.list.search', params)
    return data
  },
}
